/*
 * ************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *   Copyright 2019 Adobe Systems Incorporated
 *   All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by all applicable intellectual property
 * laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 * ************************************************************************
 */

import classNames from 'classnames';
import React, { useState } from 'react';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import QRCode from 'qrcode.react';

import { defineMessages } from '@nebula/izzy/intl';

import Button from '@react/react-spectrum/Button';
import CopyIcon from '@react/react-spectrum/Icon/Copy';
import Radio from '@react/react-spectrum/Radio';
import RadioGroup from '@react/react-spectrum/RadioGroup';
import Textfield from '@react/react-spectrum/Textfield';

import { useCopiedTimeout } from '@nebula/luna/dist/hooks/use.copied.timeout.hook';

import styles from './styles/connection.method.styl';

const MSG = defineMessages({
  copied: {
    id: 'gemini.session.copied',
    defaultMessage: 'Copied to Clipboard'
  },
  copyLinkLabel: {
    id: 'gemini.session.copy.link.label',
    defaultMessage: 'Copy Link'
  },
  label: {
    id: 'gemini.session.qr.code.label',
    defaultMessage: 'Scan QR Code'
  }
});

const connectionMethod = ({
  className,
  izzy,
  link
}) => {
  const [copied, setCopied] = useCopiedTimeout();
  const [type, setType] = useState('qr');

  return (
    <div className={classNames(styles.root, className)}>
      <RadioGroup
        defaultSelectedValue={type}
        labelsBelow={false}
        onChange={setType}
        vertical
      >
        <Radio
          data-test-id="qrRadioButton"
          label={izzy(MSG.label)}
          value="qr"
        />
        <Radio
          data-test-id="linkRadioButton"
          label={izzy(MSG.copyLinkLabel)}
          value="link"
        />
      </RadioGroup>
      <div>
        {type === 'link' && (
          <div>
            <div className={styles.copyLinkField}>
              <Textfield
                data-test-id="copyLink"
                className={classNames({ copied })}
                value={!copied ? link : izzy(MSG.copied)}
                readonly
                quiet
              />
              <CopyToClipboard
                onCopy={() => setCopied(true)}
                text={link}
              >
                <Button
                  label={null}
                  quiet
                  variant="action"
                  type="button"
                  icon={<CopyIcon />}
                />
              </CopyToClipboard>
            </div>
          </div>
        )}
        {type === 'qr' && (
          <div>
            <QRCode
              data-test-id="qrCode"
              renderAs="svg"
              value={link}
            />
          </div>
        )}
      </div>
    </div>
  );
};

connectionMethod.displayName = 'connectionMethod';
export default connectionMethod;
